// src/pages/BullyingResults.js
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import "../styles/BullyingResults.css";
import logoMirai from "../assets/mirai_logo.png";
import 'bootstrap-icons/font/bootstrap-icons.css';
import Navbar from '../components/Navbar';
import { PLATFORM_PDF_FOOTER } from '../constants/platform';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title
} from 'chart.js';
import { Doughnut, Bar } from 'react-chartjs-2';

// React-PDF
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  PDFDownloadLink,
  Image,
} from "@react-pdf/renderer";

// Register ChartJS components
ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title
);

// === Estilos PDF ===
const styles = StyleSheet.create({
  page: { padding: 40, fontSize: 12, fontFamily: "Helvetica" },
  header: { textAlign: "center", marginBottom: 20 },
  title: { fontSize: 18, fontWeight: "bold", color: "#004d40", marginTop: 10 },
  logo: { width: 60, height: 60, margin: "0 auto" },
  studentInfo: { marginBottom: 20 },
  sectionTitle: {
    fontSize: 14,
    fontWeight: "bold",
    marginTop: 15,
    marginBottom: 8,
    color: "#333",
  },
  card: {
    border: "1pt solid #ccc",
    borderRadius: 6,
    padding: 8,
    marginBottom: 10,
  },
  recommendation: { fontSize: 11, marginTop: 4, textAlign: "justify" },
  footer: {
    position: "absolute",
    bottom: 20,
    left: 40,
    right: 40,
    fontSize: 9,
    textAlign: "center",
    color: "#666",
  },
});

// === Documento PDF ===
const ResultsPDF = ({ user, results, description }) => {
  const date = new Date().toLocaleDateString();
  const recomendaciones = description[results.riskLevel] || [];

  const getLevelColor = (riskLevel) => {
    switch (riskLevel) {
      case "Bajo": return "#28a745";
      case "Moderado": return "#ffc107";
      case "Alto": return "#fd7e14";
      case "Grave": return "#dc3545";
      default: return "#6c757d";
    }
  };

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <Image
          src={logoMirai}
          style={{
            position: "absolute",
            top: "30%",
            left: "20%",
            width: 300,
            height: 300,
            opacity: 0.1,
          }}
        />

        <View style={styles.header}>
          <Image src={logoMirai} style={styles.logo} />
          <Text style={styles.title}>Resultados de Acoso, Violencia y Bullying</Text>
        </View>

        <View style={styles.studentInfo}>
          <Text>Nombre del estudiante: {user?.name || "N/D"}</Text>
          <Text>Fecha: {date}</Text>
          <Text>Puntaje Total: {results.totalScore} / 120</Text>
          <Text style={{ color: getLevelColor(results.riskLevel), fontWeight: "bold" }}>
            Nivel de riesgo: {results.riskLevel}
          </Text>
        </View>

        <Text style={styles.sectionTitle}>Recomendaciones:</Text>
        <View style={styles.card}>
          {recomendaciones.map((rec, idx) => (
            <Text key={idx} style={styles.recommendation}>• {rec}</Text>
          ))}
        </View>

        <Text style={styles.footer}>
          {PLATFORM_PDF_FOOTER}
        </Text>
      </Page>
    </Document>
  );
};

const BullyingResults = () => {
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [activeSection, setActiveSection] = useState("overview");
  const navigate = useNavigate();

  const description = {
    "Bajo": [
      "Continúa fomentando un entorno de respeto y empatía.",
      "Participa en actividades de sensibilización."
    ],
    "Moderado": [
      "Busca orientación con docentes o psicólogos escolares.",
      "Identifica situaciones de riesgo y repórtalas oportunamente."
    ],
    "Alto": [
      "Solicita apoyo especializado para atender las experiencias vividas.",
      "Participa en programas de acompañamiento psicológico y emocional.",
      "Reporta inmediatamente cualquier situación de violencia."
    ],
    "Grave": [
      "Se recomienda atención inmediata por parte del equipo de apoyo escolar.",
      "Debe activarse un protocolo de atención y seguridad.",
      "Consulta urgente con profesionales especializados en violencia escolar."
    ]
  };

  useEffect(() => {
    const fetchResults = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/users/me`, {
          headers: { Authorization: token },
        });
        setResults(response.data.BullyingResults || {});
        setUser(response.data);
      } catch (error) {
        console.error("Error al obtener resultados:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchResults();
  }, []);

  const getLevelColor = (riskLevel) => {
    switch (riskLevel) {
      case "Bajo": return "#28a745";
      case "Moderado": return "#ffc107";
      case "Alto": return "#fd7e14";
      case "Grave": return "#dc3545";
      default: return "#6c757d";
    }
  };

  const getLevelIcon = (riskLevel) => {
    switch (riskLevel) {
      case "Bajo": return "bi-shield-check";
      case "Moderado": return "bi-exclamation-circle";
      case "Alto": return "bi-exclamation-triangle-fill";
      case "Grave": return "bi-shield-exclamation";
      default: return "bi-question-diamond";
    }
  };

  // 120 es el puntaje máximo mencionado en la lógica anterior
  const MAX_SCORE = 120;

  // Datos para gráfico circular
  const scoreData = {
    labels: ['Puntaje', 'Restante'],
    datasets: [
      {
        data: [results?.totalScore || 0, MAX_SCORE - (results?.totalScore || 0)],
        backgroundColor: [
          getLevelColor(results?.riskLevel),
          '#e9ecef'
        ],
        borderWidth: 0,
        circumference: 180,
        rotation: 270,
      },
    ],
  };

  // Datos para gráfico de barras
  const comparisonData = {
    labels: ['Tu Puntaje', 'Riesgo Bajo', 'Riesgo Grave'],
    datasets: [
      {
        label: 'Puntaje',
        data: [results?.totalScore || 0, 30, 90], // Valores de referencia aproximados para comparación
        backgroundColor: [
          getLevelColor(results?.riskLevel),
          '#28a745',
          '#dc3545'
        ],
        borderWidth: 0,
        borderRadius: 5,
        barPercentage: 0.6,
      },
    ],
  };

  return (
    <>
      <Navbar />
      <div className="dashboard-layout">
        {/* Sidebar Standard */}
        <div className="dashboard-sidebar">
          <div className="sidebar-header">
            <h3>Acoso y Bullying</h3>
          </div>
          <div className="sidebar-menu">
            <button
              className={`menu-item ${activeSection === 'overview' ? 'active' : ''}`}
              onClick={() => setActiveSection('overview')}
            >
              <i className="bi bi-speedometer2"></i> Resumen
            </button>
            <button
              className="menu-item return-dashboard"
              onClick={() => navigate('/dashboard')}
            >
              <i className="bi bi-arrow-left-circle"></i> Volver al Dashboard
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div className="dashboard-main">
          <div className="br-container">
            {loading ? (
              <div className="br-loading">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Cargando...</span>
                </div>
                <p>Analizando datos de seguridad escolar...</p>
              </div>
            ) : !results || results.totalScore === undefined ? (
              <div className="br-empty">
                <i className="bi bi-shield-x"></i>
                <h3>Sin resultados disponibles</h3>
                <p>No has completado esta evaluación aún.</p>
                <button className="btn btn-primary" onClick={() => navigate("/dashboard")}>
                  Ir al Dashboard
                </button>
              </div>
            ) : (
              <>
                <div className="br-header">
                  <h2><i className="bi bi-shield-lock"></i> Resultados de Seguridad Escolar</h2>
                  <p>Análisis de riesgo sobre Acoso, Violencia y Bullying</p>
                </div>

                {/* Top Stats Grid */}
                <div className="br-stats-grid">
                  <div className="br-stat-card primary-card">
                    <div className="icon-wrapper">
                      <i className="bi bi-clipboard-data"></i>
                    </div>
                    <div className="content-wrapper">
                      <h3>{results.totalScore}<span className="small-text">/{MAX_SCORE}</span></h3>
                      <p>Puntaje Global</p>
                    </div>
                  </div>

                  <div className="br-stat-card risk-card" style={{ borderLeftColor: getLevelColor(results.riskLevel) }}>
                    <div className="icon-wrapper" style={{ color: getLevelColor(results.riskLevel), background: `${getLevelColor(results.riskLevel)}20` }}>
                      <i className={`bi ${getLevelIcon(results.riskLevel)}`}></i>
                    </div>
                    <div className="content-wrapper">
                      <h3 style={{ color: getLevelColor(results.riskLevel) }}>{results.riskLevel}</h3>
                      <p>Nivel de Riesgo</p>
                    </div>
                  </div>

                  <div className="br-stat-card date-card">
                    <div className="icon-wrapper">
                      <i className="bi bi-calendar4-week"></i>
                    </div>
                    <div className="content-wrapper">
                      <h3>{new Date().toLocaleDateString()}</h3>
                      <p>Fecha Evaluación</p>
                    </div>
                  </div>
                </div>

                {/* Charts Grid */}
                <div className="br-charts-grid">
                  <div className="br-chart-card">
                    <div className="card-title">
                      <h4><i className="bi bi-speedometer"></i> Indicador de Seguridad</h4>
                    </div>
                    <div className="gauge-wrapper">
                      <div className="gauge-chart">
                        <Doughnut
                          data={scoreData}
                          options={{
                            cutout: '75%',
                            plugins: { legend: { display: false }, tooltip: { enabled: false } },
                            maintainAspectRatio: false
                          }}
                        />
                        <div className="gauge-center-text">
                          <h2>{Math.round((results.totalScore / MAX_SCORE) * 100)}%</h2>
                        </div>
                      </div>
                      <p className="chart-note">Porcentaje relativo de riesgo detectado</p>
                    </div>
                  </div>

                  <div className="br-chart-card">
                    <div className="card-title">
                      <h4><i className="bi bi-bar-chart"></i> Comparativo de Niveles</h4>
                    </div>
                    <div className="bar-wrapper">
                      <Bar
                        data={comparisonData}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          plugins: { legend: { display: false } },
                          scales: { y: { beginAtZero: true, max: MAX_SCORE } }
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Recommendations Section */}
                <div className="br-recommendations">
                  <h4><i className="bi bi-list-check"></i> Recomendaciones Personalizadas</h4>
                  <div className="br-rec-grid">
                    {/* Render recommendations from description object */}
                    {description[results.riskLevel]?.map((rec, idx) => (
                      <div key={idx} className={`br-rec-card ${results.riskLevel === "Bajo" ? "success" :
                          results.riskLevel === "Moderado" ? "warning" : "danger"
                        }`}>
                        <i className={`bi ${results.riskLevel === "Bajo" ? "bi-check-circle-fill" :
                            results.riskLevel === "Moderado" ? "bi-exclamation-circle-fill" : "bi-exclamation-triangle-fill"
                          }`}></i>
                        <div>
                          <h5>Recomendación #{idx + 1}</h5>
                          <p>{rec}</p>
                        </div>
                      </div>
                    ))}

                    {/* Tarjeta estática de ayuda siempre visible */}
                    <div className="br-rec-card info">
                      <i className="bi bi-info-circle-fill"></i>
                      <div>
                        <h5>Recursos de Apoyo</h5>
                        <p>Recuerda que siempre puedes acudir a orientación escolar si necesitas ser escuchado.</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Footer / PDF Download */}
                <div className="br-footer">
                  <PDFDownloadLink
                    document={<ResultsPDF user={user} results={results} description={description} />}
                    fileName={`Resultados_Bullying_${new Date().toLocaleDateString().replaceAll("/", "_")}.pdf`}
                    className="text-decoration-none"
                  >
                    {({ loading }) => (
                      <button className="btn btn-primary br-btn" disabled={loading}>
                        <i className={`bi ${loading ? 'bi-hourglass-split' : 'bi-file-earmark-pdf'}`}></i>
                        {loading ? ' Generando PDF...' : ' Descargar Resultados Completos'}
                      </button>
                    )}
                  </PDFDownloadLink>
                </div>

              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default BullyingResults;
